import type { ReactNode } from 'react'
import { Link } from 'wouter'
import { FileText, ArrowLeft } from '@/components/ds/icons'
import { Separator } from '@/components/ui/separator'

interface LegalLayoutProps {
  title: string
  /** Fecha de la última actualización en formato legible (ej: "5 de junio de 2026"). */
  lastUpdated: string
  children: ReactNode
}

/**
 * Contenedor compartido para las páginas legales públicas (Términos y Privacidad).
 * Replica el estilo de las páginas de autenticación pero con un ancho mayor
 * (max-w-3xl) pensado para lectura de texto largo.
 */
export function LegalLayout({ title, lastUpdated, children }: LegalLayoutProps) {
  return (
    <div className="min-h-screen bg-canvas py-12 px-4">
      <div className="mx-auto w-full max-w-3xl">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <FileText className="h-5 w-5" />
          </div>
          <span className="text-lg font-bold tracking-tight">Facil Control</span>
        </div>

        <Link
          href="/login"
          className="mb-8 inline-flex items-center gap-1 text-sm text-primary hover:underline"
        >
          <ArrowLeft className="h-3 w-3" />
          Volver al inicio
        </Link>

        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Última actualización: {lastUpdated}
        </p>

        <Separator className="my-8" />

        <div className="space-y-8 text-sm leading-relaxed text-foreground">
          {children}
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col items-center gap-2 text-center text-xs text-muted-foreground">
          <p>
            <Link href="/terminos" className="hover:underline">
              Términos y Condiciones
            </Link>
            {' · '}
            <Link href="/privacidad" className="hover:underline">
              Política de Privacidad
            </Link>
          </p>
          <p>© {new Date().getFullYear()} Facil Control. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  )
}

/** Sección de un documento legal: encabezado numerado + contenido. */
export function LegalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      <div className="space-y-3 text-muted-foreground">{children}</div>
    </section>
  )
}
